<!-- eslint-disable vue/no-bare-strings-in-template, @intlify/vue-i18n/no-raw-text -->
<script setup>
import { computed } from 'vue';
import LaisPrimaryButton from 'dashboard/components-next/LaisPrimaryButton.vue';
import LaisStatusBadge from 'dashboard/components-next/LaisStatusBadge.vue';
import { formatDate } from '../utils/offerFormatters';

const props = defineProps({
  context: { type: Object, default: null },
  loading: { type: Boolean, default: false },
  errorMessage: { type: String, default: '' },
});

const emit = defineEmits(['retry']);

const tasks = computed(() => props.context?.crm_tasks || {});
const executions = computed(() => props.context?.automation_executions || {});
const taskItems = computed(() => tasks.value.items || []);
const executionItems = computed(() => executions.value.items || []);

const formatDateTime = value => {
  if (!value) return '—';
  const date = new Date(value);
  if (Number.isNaN(date.getTime())) return String(value);
  return new Intl.DateTimeFormat('pt-BR', {
    day: '2-digit',
    month: '2-digit',
    year: 'numeric',
    hour: '2-digit',
    minute: '2-digit',
  }).format(date);
};

const statusLabel = value => {
  const labels = {
    open: 'Aberta',
    in_progress: 'Em andamento',
    waiting: 'Aguardando',
    completed: 'Concluída',
    canceled: 'Cancelada',
    pending: 'Pendente',
    planned: 'Planejada',
    running: 'Executando',
    succeeded: 'Concluída',
    partially_succeeded: 'Parcial',
    failed: 'Falhou',
    blocked: 'Bloqueada',
    quarantined: 'Em quarentena',
    compensating: 'Compensando',
    compensated: 'Compensada',
    canceled_execution: 'Cancelada',
  };
  return labels[value] || value || 'Indefinido';
};

const statusTone = value => {
  if (['completed', 'succeeded', 'compensated'].includes(value))
    return 'positive';
  if (['failed', 'blocked', 'quarantined', 'canceled'].includes(value))
    return 'danger';
  if (['waiting', 'pending', 'planned', 'compensating'].includes(value))
    return 'warning';
  if (['in_progress', 'running'].includes(value)) return 'info';
  return 'neutral';
};

const taskTypeLabel = value =>
  String(value || 'Tarefa operacional')
    .replaceAll('_', ' ')
    .replace(/\b\w/g, character => character.toUpperCase());

const waitingOnLabel = value => {
  const labels = {
    agent: 'corretor',
    customer: 'cliente',
    partner: 'parceiro',
    system: 'sistema',
    none: 'sem dependência',
  };
  return labels[value] || value;
};

const priorityLabel = value => {
  const labels = {
    low: 'baixa',
    normal: 'normal',
    high: 'alta',
    urgent: 'urgente',
  };
  return labels[value] || value;
};

const executionAggregateLabel = execution => {
  const labels = {
    PropertyOffer: 'proposta',
    CrmTask: 'tarefa',
    PropertyContract: 'contrato',
    KycVerification: 'KYC',
    PropertyDiligenceCase: 'diligência',
    PropertyTransaction: 'transação',
    TransactionChecklistItem: 'checklist',
    ReservationHold: 'reserva',
  };
  return (
    labels[execution?.aggregate_type] || execution?.aggregate_type || 'registro'
  );
};
</script>

<template>
  <!-- eslint-disable vue/no-bare-strings-in-template, @intlify/vue-i18n/no-raw-text -->
  <section
    class="offer-operational-panel min-w-0 rounded-lais border border-lais-border bg-white p-4 sm:p-5"
    aria-labelledby="offer-operational-panel-title"
  >
    <div class="flex min-w-0 flex-wrap items-start justify-between gap-3">
      <div class="min-w-0">
        <p
          class="text-[11px] font-semibold uppercase tracking-[0.14em] text-lais-text-muted"
        >
          Operação
        </p>
        <h3
          id="offer-operational-panel-title"
          class="mt-1 font-redHatDisplay text-[17px] font-semibold text-lais-text"
        >
          Tarefas e automações
        </h3>
        <p
          class="mt-1 max-w-2xl text-[12px] leading-relaxed text-lais-text-muted"
        >
          Acompanhe o que precisa de ação e o que já foi processado pelo motor
          de regras.
        </p>
      </div>
      <LaisPrimaryButton
        v-if="errorMessage"
        size="sm"
        variant="ghost"
        @click="emit('retry')"
      >
        Tentar novamente
      </LaisPrimaryButton>
    </div>

    <div
      v-if="errorMessage"
      class="mt-4 flex items-start gap-2 rounded-lais-sm border border-rose-200 bg-rose-50 px-3 py-2 text-[12px] leading-relaxed text-rose-700"
      role="alert"
    >
      <span
        class="i-lucide-triangle-alert mt-0.5 size-4 shrink-0"
        aria-hidden="true"
      />
      <span>{{ errorMessage }}</span>
    </div>

    <div
      v-if="loading"
      class="mt-5 grid gap-3"
      aria-label="Carregando operação"
    >
      <div
        v-for="index in 3"
        :key="index"
        class="animate-pulse rounded-lais-sm border border-lais-border-soft p-3"
      >
        <div class="h-3 w-2/5 rounded bg-lais-bg-soft" />
        <div class="mt-2 h-4 w-4/5 rounded bg-lais-bg-soft" />
        <div class="mt-3 h-3 w-3/5 rounded bg-lais-bg-soft" />
      </div>
    </div>

    <div
      v-else
      class="offer-operational-panel__content mt-5 grid min-w-0 gap-5"
    >
      <section class="min-w-0" aria-labelledby="offer-crm-tasks-title">
        <div class="flex flex-wrap items-center justify-between gap-2">
          <h4
            id="offer-crm-tasks-title"
            class="font-redHatDisplay text-[15px] font-semibold text-lais-text"
          >
            Tarefas da proposta
          </h4>
          <div class="flex flex-wrap gap-2 text-[11px] font-semibold">
            <span
              class="rounded-full bg-lais-bg-soft px-2.5 py-1 text-lais-text-muted"
            >
              {{ tasks.total_count || 0 }} no total
            </span>
            <span class="rounded-full bg-amber-50 px-2.5 py-1 text-amber-800">
              {{ tasks.open_count || 0 }} em aberto
            </span>
          </div>
        </div>

        <div
          v-if="tasks.available === false"
          class="mt-3 rounded-lais-sm border border-dashed border-lais-border bg-lais-bg-soft px-3 py-4 text-[12px] text-lais-text-muted"
        >
          As tarefas desta proposta não estão disponíveis para este perfil.
        </div>
        <div
          v-else-if="!taskItems.length"
          class="mt-3 rounded-lais-sm border border-dashed border-lais-border bg-lais-bg-soft px-3 py-4 text-[12px] text-lais-text-muted"
        >
          Nenhuma tarefa operacional foi criada para esta proposta.
        </div>
        <div v-else class="mt-3 grid min-w-0 gap-2.5">
          <article
            v-for="task in taskItems"
            :key="task.id"
            class="min-w-0 rounded-lais-sm border border-lais-border-soft bg-lais-bg-soft/40 p-3"
          >
            <div
              class="flex min-w-0 flex-wrap items-start justify-between gap-2"
            >
              <div class="min-w-0">
                <p
                  class="text-[11px] font-semibold uppercase tracking-wide text-lais-text-muted"
                >
                  {{ taskTypeLabel(task.task_type) }}
                </p>
                <h5
                  class="mt-1 break-words text-[13px] font-semibold leading-snug text-lais-text"
                >
                  {{ task.title }}
                </h5>
              </div>
              <LaisStatusBadge
                :label="statusLabel(task.status)"
                :tone="statusTone(task.status)"
                size="sm"
                dot
              />
            </div>
            <dl
              class="mt-3 grid min-w-0 gap-2 text-[12px] sm:grid-cols-2 sm:gap-x-4"
            >
              <div
                v-if="task.waiting_on && task.waiting_on !== 'none'"
                class="min-w-0"
              >
                <dt class="text-lais-text-muted">Aguardando</dt>
                <dd class="mt-0.5 break-words font-semibold text-lais-text">
                  {{ waitingOnLabel(task.waiting_on) }}
                </dd>
              </div>
              <div v-if="task.priority" class="min-w-0">
                <dt class="text-lais-text-muted">Prioridade</dt>
                <dd
                  class="mt-0.5 break-words font-semibold capitalize text-lais-text"
                >
                  {{ priorityLabel(task.priority) }}
                </dd>
              </div>
              <div v-if="task.due_at" class="min-w-0">
                <dt class="text-lais-text-muted">Prazo</dt>
                <dd class="mt-0.5 break-words font-semibold text-lais-text">
                  {{ formatDate(task.due_at) }}
                </dd>
              </div>
              <div v-if="task.assignee?.name" class="min-w-0">
                <dt class="text-lais-text-muted">Responsável</dt>
                <dd class="mt-0.5 break-words font-semibold text-lais-text">
                  {{ task.assignee.name }}
                </dd>
              </div>
            </dl>
          </article>
        </div>
      </section>

      <section
        class="min-w-0"
        aria-labelledby="offer-automation-executions-title"
      >
        <div class="flex flex-wrap items-center justify-between gap-2">
          <h4
            id="offer-automation-executions-title"
            class="font-redHatDisplay text-[15px] font-semibold text-lais-text"
          >
            Histórico de automações
          </h4>
          <span
            class="rounded-full bg-lais-bg-soft px-2.5 py-1 text-[11px] font-semibold text-lais-text-muted"
          >
            {{ executions.total_count || 0 }} execuções
          </span>
        </div>

        <div
          v-if="executions.available === false"
          class="mt-3 rounded-lais-sm border border-dashed border-lais-border bg-lais-bg-soft px-3 py-4 text-[12px] leading-relaxed text-lais-text-muted"
        >
          O histórico técnico de automações fica disponível somente para
          administradores.
        </div>
        <div
          v-else-if="!executionItems.length"
          class="mt-3 rounded-lais-sm border border-dashed border-lais-border bg-lais-bg-soft px-3 py-4 text-[12px] text-lais-text-muted"
        >
          Nenhuma automação relacionada foi executada ainda.
        </div>
        <div v-else class="mt-3 grid min-w-0 gap-2.5">
          <article
            v-for="execution in executionItems"
            :key="execution.id"
            class="min-w-0 rounded-lais-sm border border-lais-border-soft bg-lais-bg-soft/40 p-3"
          >
            <div
              class="flex min-w-0 flex-wrap items-start justify-between gap-2"
            >
              <div class="min-w-0">
                <p
                  class="break-words text-[13px] font-semibold leading-snug text-lais-text"
                >
                  {{ execution.rule?.name || execution.event_name }}
                </p>
                <p class="mt-1 break-words text-[11px] text-lais-text-muted">
                  Evento: {{ execution.event_name }} ·
                  {{ executionAggregateLabel(execution) }}
                </p>
              </div>
              <LaisStatusBadge
                :label="statusLabel(execution.status)"
                :tone="statusTone(execution.status)"
                size="sm"
                dot
              />
            </div>
            <p
              v-if="
                execution.finished_at ||
                execution.started_at ||
                execution.reserved_at
              "
              class="mt-3 text-[12px] text-lais-text-muted"
            >
              {{
                formatDateTime(
                  execution.finished_at ||
                    execution.started_at ||
                    execution.reserved_at
                )
              }}
            </p>
          </article>
        </div>
      </section>
    </div>
  </section>
</template>

<style scoped>
.offer-operational-panel {
  container-type: inline-size;
}

@container (min-width: 48rem) {
  .offer-operational-panel__content {
    grid-template-columns: minmax(0, 1.15fr) minmax(0, 0.85fr);
    align-items: start;
    gap: 1.5rem;
  }
}
</style>
